import { useEffect, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { addBundle } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting';
import { useBrowserFormatter } from '../../locale/formatting/BrowserFormatter';
import { getTextLocalizer } from '../../locale/useTextLocalizer';
import { getNumberSeparators, parseAndFormatInput, validateNumericInput } from './useInputValidationAdapter';
import bundle from './NovaComponents-Input-gui-icu.properties';
addBundle(bundle);
let localize;
getTextLocalizer('NovaComponents-Input-gui-icu').then(localizer => {
  localize = localizer;
});

/**
 * This is a range validator for setting min and max values.
 * @param props  of type ValidateRangeInputProps
 * @returns values{rawValue, formatValue}, isError, status, message
 *
 */
export const validateRangeInput = props => {
  let isError = false,
    status = undefined,
    message = undefined;
  const incMode = props.inclusiveMode >= 0 && props.inclusiveMode <= 3 ? props.inclusiveMode : 3;

  // to validate numeric value
  const result = validateNumericInput(props.value, props.appLocale, props.isRequired, props.allowIntegersOnly, props.isLive, props.minLength, props.numberFormatOptions, props.parseAndFormatFunction, props.formatter);
  if (result.isError) {
    isError = result.isError;
    status = result.status;
    message = result.message;
  } else {
    if (props.value) {
      const parsedValue = result.values.rawValue;
      const maxValueLocaleString = props.formatter && props.maxValue ? props.formatter?.formatNumeric(props.maxValue, undefined, props.numberFormatOptions) : props.maxValue?.toLocaleString(props.appLocale, props.numberFormatOptions);
      const minValueLocaleString = props.formatter && props.minValue ? props.formatter?.formatNumeric(props.minValue, undefined, props.numberFormatOptions) : props.minValue?.toLocaleString(props.appLocale, props.numberFormatOptions);
      if (parsedValue > props.maxValue) {
        if (incMode < 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeExclusive.msg', {
            maxValue: maxValueLocaleString
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.maxRangeInclusive.msg', {
            maxValue: maxValueLocaleString
          });
        }
      } else if (parsedValue === props.maxValue && incMode < 2) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.maxRangeExclusive.msg', {
          maxValue: maxValueLocaleString
        });
      } else if (parsedValue < props.minValue) {
        if (incMode === 0 || incMode === 2) {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeExclusive.msg', {
            minValue: minValueLocaleString
          });
        } else {
          isError = true;
          status = 'alert';
          message = localize('rangeValidator.minRangeInclusive.msg', {
            minValue: minValueLocaleString
          });
        }
      } else if (parsedValue === props.minValue && !(incMode === 1 || incMode === 3)) {
        isError = true;
        status = 'alert';
        message = localize('rangeValidator.minRangeExclusive.msg', {
          minValue: minValueLocaleString
        });
      }
    }
  }
  return {
    isError,
    status,
    message,
    values: result.values
  };
};
/**
 * This hook provide functionality to handle events such as onChange,onBlur
 * & onCancel with empty initial value.
 * For those who doesn't wish to use this,
 * @function validateRangeInput is available for consumption
 * @param props
 *
 */
export const useRangeValidationAdapter = props => {
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const appLocale = props.appLocale ?? useFormatLocale();
  const formatter = props.formatter ?? useBrowserFormatter(appLocale);
  const initialValue = props.initialValue ? formatter?.formatNumeric(Number(props.initialValue), undefined, props.numberFormatOptions) : '';
  const [value, setValue] = useState(initialValue ?? '');
  const [rawValue, setRawValue] = useState(initialValue ? Number(props.initialValue) : undefined);
  const [currentLocale, setCurrentLocale] = useState('');
  const [errors, setErrors] = useState({
    status: undefined,
    message: ''
  });
  const handleChange = event => {
    const liveValue = event.value;
    const [decimalSeparator] = getNumberSeparators(appLocale);
    const decimalValue = liveValue.lastIndexOf(decimalSeparator) !== -1 ? liveValue.slice(liveValue.lastIndexOf(decimalSeparator) + 1) : '';
    const result = props.validateInputFunction(liveValue, appLocale, props.isRequired, props.allowIntegersOnly, true, props.minLength, props.numberFormatOptions);
    setRawValue(result.values.rawValue);
    result.isError ? setErrors({
      status: result.status,
      message: result.message
    }) : setErrors({
      status: undefined,
      message: undefined
    });
    if (decimalValue !== '' && props.numberFormatOptions?.maximumFractionDigits && decimalValue.length > props.numberFormatOptions?.maximumFractionDigits) {
      earcon.play();
    } else if (result.status === 'alert') {
      earcon.play();
      setValue(liveValue);
    } else setValue(liveValue);
  };
  const handleCommit = e => {
    return handleBlurAndFocus(false, e);
  };
  const getSeparators = formatter => {
    const decimalSeparator = formatter?.formatNumeric(1.1).replace(/1/gu, '');
    let groupingSeparator = formatter?.formatNumeric(11111).replace(/1/gu, '');
    const sSpaceRegex = /^\s$/;

    // if grouping separator is no-break space (French) then replace the space characters from user input with no-break space
    if (groupingSeparator && sSpaceRegex.test(groupingSeparator)) {
      groupingSeparator = '\\s';
    }
    return [decimalSeparator, groupingSeparator];
  };
  const handleBlurAndFocus = (focused, e) => {
    if (isNullish(props.minValue) && isNullish(props.maxValue)) {
      return;
    }
    const result = validateRangeInput({
      value,
      minValue: props.minValue,
      maxValue: props.maxValue,
      inclusiveMode: props.inclusiveMode,
      appLocale,
      isRequired: props.isRequired,
      allowIntegersOnly: props.allowIntegersOnly,
      isLive: false,
      minLength: props.minLength,
      numberFormatOptions: props.numberFormatOptions,
      formatter
    });
    setCurrentLocale(appLocale);
    setRawValue(undefined);
    if (result.isError) {
      if (e?.reason === 'enter') earcon.play();
      setValue(value);
    } else if (value === '') {
      setValue('');
    } else if (!focused && !props.disableAutoFormatting) {
      setValue(result.values.formatValue);
    } else if (focused && !props.disableAutoFormatting) {
      const separators = getSeparators(formatter);
      const formatValueWithoutGroupingSeparator = result.values.formatValue.replace(new RegExp(`[${separators[1]}]`, 'gu'), '');
      setValue(formatValueWithoutGroupingSeparator);
    } else {
      setValue(value);
      setRawValue(result.values.rawValue);
    }
    result.isError ? setErrors({
      status: result.status,
      message: result.message
    }) : setErrors({
      status: undefined,
      message: undefined
    });
  };
  const handleFocus = () => {
    return handleBlurAndFocus(true);
  };
  const handleBlur = handleCommit;
  const handleCancel = () => {
    setValue('');
    setRawValue(undefined);
    setErrors({
      status: undefined,
      message: undefined
    });
  };
  const revalidateRef = useRef();
  const isInitializedRef = useRef(false);
  revalidateRef.current = handleBlur;
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      return;
    }
    revalidateRef.current?.();
  }, [props.minValue, props.maxValue, props.inclusiveMode, props.isRequired, props.allowIntegersOnly, props.minLength, props.numberFormatOptions?.maximumFractionDigits, props.numberFormatOptions?.minimumFractionDigits]);
  useEffect(() => {
    if (!initialValue || errors.status || appLocale === currentLocale) return;
    setCurrentLocale(appLocale);
    const result = parseAndFormatInput(rawValue ? formatter.formatNumeric(rawValue) : initialValue, appLocale, props.numberFormatOptions, formatter);
    if (result && !isNaN(parseFloat(result.formatValue))) {
      setValue(result.formatValue);
      setRawValue(result.rawValue);
    }
  }, [appLocale, currentLocale, rawValue, errors.status, initialValue, props.numberFormatOptions, formatter]);
  return {
    handleChange,
    value,
    errors,
    handleBlur,
    handleCancel,
    handleCommit,
    handleFocus
  };
};
//# sourceMappingURL=useRangeValidationAdapter.js.map